import { useContext } from 'react';
import { AppContext } from '../../../../common/context/';
import { Modal } from '../BaseModal';

export interface CostCenterScheduleResult {
    employeeId: number;
    employeeFullName: string;
    date: string;
    costCenterId: number;
    costCenterName: string;
    startDate: string;
    endDate: string;
    comment: string;
}

interface CostCenterScheduleResultsModalProps {
    isOpen: boolean;
    acceptedResults: CostCenterScheduleResult[];
    rejectedResults: CostCenterScheduleResult[];
    onClose: (state: boolean) => void;
}

export const CostCenterScheduleResultsModal = ({
    isOpen,
    acceptedResults,
    rejectedResults,
    onClose,
}: CostCenterScheduleResultsModalProps) => {
    const { langResources } = useContext(AppContext);

    const handleClose = () => {
        onClose(false);
    };

    const formatTime = (dateTime: string) => {
        if (/^\d{2}:\d{2}(:\d{2})?$/.test(dateTime)) {
            return dateTime.slice(0, 5);
        }
        try {
            const date = new Date(dateTime);
            return date.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
        } catch {
            return dateTime;
        }
    };

    const formatDateOnly = (dateTime: string) => {
        try {
            const date = new Date(dateTime);
            return date.toLocaleDateString('es-CO', { timeZone: 'UTC' });
        } catch {
            return dateTime;
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            modalTitle={langResources['cost.center.modal'].results.title}
            onClose={handleClose}
            onSubmit={handleClose}
            disableSubmit={false}
            widthCustom='1200px'
        >
            <div className='modal-body text-black'>
                <div className="mb-4"
                    style={{
                        background: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: '0.25rem',
                        padding: '1rem'
                    }}>
                    <div className='row'>
                        <div className='col-4'>
                            <strong>{langResources['cost.center.modal'].results.total}</strong> {acceptedResults.length + rejectedResults.length}
                        </div>
                        <div className='col-4'>
                            <strong className='text-success'>{langResources['cost.center.modal'].results.successful.count}</strong> {acceptedResults.length}
                        </div>
                        <div className='col-4'>
                            <strong className='text-danger'>{langResources['cost.center.modal'].results.rejected.count}</strong> {rejectedResults.length}
                        </div>
                    </div>
                </div>

                {acceptedResults.length > 0 && (
                    <>
                        <h5 className='text-success mb-3'>
                            {langResources['cost.center.modal'].results.successful.title} ({acceptedResults.length})
                        </h5>
                        <div style={{ maxHeight: '30vh', overflowY: 'auto', marginBottom: '20px' }}>
                            <table className='table table-sm table-hover'>
                                <thead className='table-light position-sticky' style={{ top: '0px' }}>
                                    <tr>
                                        <th>{langResources['cost.center.modal'].results.table.employee}</th>
                                        <th>{langResources['cost.center.modal'].results.table.date}</th>
                                        <th>{langResources['cost.center.modal'].results.table.costCenter}</th>
                                        <th>{langResources['cost.center.modal'].results.table.timeRange}</th>
                                        <th>{langResources['cost.center.modal'].results.table.comment}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {acceptedResults.map((result, index) => (
                                        <tr key={`accepted-${index}`}>
                                            <td><strong>{result.employeeFullName}</strong></td>
                                            <td>{formatDateOnly(result.date)}</td>
                                            <td>{result.costCenterName} ({result.costCenterId})</td>
                                            <td>
                                                {formatTime(result.startDate)} - {formatTime(result.endDate)}
                                            </td>
                                            <td>{result.comment}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {rejectedResults.length > 0 && (
                    <>
                        <h5 className='text-danger mb-3'>
                            {langResources['cost.center.modal'].results.rejected.title} ({rejectedResults.length})
                        </h5>
                        <div style={{ maxHeight: '30vh', overflowY: 'auto' }}>
                            <table className='table table-sm table-hover'>
                                <thead className='table-light position-sticky' style={{ top: '0px' }}>
                                    <tr>
                                        <th>{langResources['cost.center.modal'].results.table.employee}</th>
                                        <th>{langResources['cost.center.modal'].results.table.date}</th>
                                        <th>{langResources['cost.center.modal'].results.table.costCenter}</th>
                                        <th>{langResources['cost.center.modal'].results.table.timeRange}</th>
                                        <th>{langResources['cost.center.modal'].results.table.rejectionReason}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rejectedResults.map((result, index) => (
                                        <tr key={`rejected-${index}`}>
                                            <td><strong>{result.employeeFullName}</strong></td>
                                            <td>{formatDateOnly(result.date)}</td>
                                            <td>{result.costCenterName} ({result.costCenterId})</td>
                                            <td>
                                                {formatTime(result.startDate)} - {formatTime(result.endDate)}
                                            </td>
                                            <td>{result.comment}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {acceptedResults.length === 0 && rejectedResults.length === 0 && (
                    <div className='alert alert-warning'>
                        {langResources['cost.center.modal'].results.empty}
                    </div>
                )}
            </div>
        </Modal>
    );
};